import React from "react";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { DashCostBreakdownSkeleton } from "./DashCardSkeletons";
import { getAmount } from "../../../../utils/helper";

function formatComparePct(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return null;
  const n = Number(pct);
  const arrow = n > 0 ? "↑" : n < 0 ? "↓" : "→";
  return `${arrow}${Math.abs(n)}%`;
}

/**
 * Cost breakdown list with proportional bars (Amazon fees, COGS, ads, net tax, expenses).
 */
export default function DashCostBreakdownCard({
  loading,
  costBreakdown,
  currencySign = "₹",
  netTaxTooltip,
}) {
  const items = costBreakdown?.items || [];
  const total = Number(costBreakdown?.total) || 0;

  return (
    <div className="cost-breakdown-card card border shadow-none h-100 overflow-hidden rounded-3">
      <div className="card-body d-flex flex-column p-5 pb-0">
        <h3 className="cost-breakdown-card__title fw-bolder mb-0 fs-5">Cost Breakdown</h3>

        <div className="flex-grow-1 mt-5">
          {loading ? (
            <DashCostBreakdownSkeleton />
          ) : (
            <ul className="list-unstyled mb-0">
              {items.map((row, idx) => {
                const amt = Number(row.amount) || 0;
                const pct = total > 0 ? Math.min(100, (amt / total) * 100) : 0;
                const display = getAmount(-amt, currencySign, { decimals: 2 });
                const compareAmount = row.compare?.delta;
                const comparePct = formatComparePct(row.compare?.pct);
                return (
                  <li
                    key={row.key}
                    className={`cost-breakdown-card__row ${idx > 0 ? "cost-breakdown-card__row--divider pt-5 mt-5" : ""}`}
                  >
                    <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
                      <div className="d-flex align-items-center gap-2 min-w-0">
                        <span
                          className="cost-breakdown-card__swatch flex-shrink-0 rounded-1"
                          style={{ backgroundColor: row.color }}
                          aria-hidden
                        />
                        <span className="cost-breakdown-card__label fw-semibold fs-7 text-truncate">
                          {row.label}
                          {row.key === "net_tax" && netTaxTooltip ? (
                            <Tooltip title={netTaxTooltip} placement="top">
                              <span className="ms-2 text-muted" style={{ cursor: "help" }}>
                                <InfoCircleOutlined style={{ fontSize: "0.75rem" }} />
                              </span>
                            </Tooltip>
                          ) : null}
                        </span>
                      </div>
                      <div className="text-end flex-shrink-0">
                        <div className="cost-breakdown-card__amount fw-bold fs-7">{display}</div>
                        {compareAmount != null || comparePct ? (
                          <div className="cost-breakdown-card__compare">
                            {compareAmount != null ? (
                              <span>{getAmount(-compareAmount, currencySign, { decimals: 2 })}</span>
                            ) : null}
                            {comparePct ? (
                              <span
                                className={
                                  row.compare?.pct > 0
                                    ? "cost-breakdown-card__compare-pct cost-breakdown-card__compare-pct--up"
                                    : row.compare?.pct < 0
                                      ? "cost-breakdown-card__compare-pct cost-breakdown-card__compare-pct--down"
                                      : "cost-breakdown-card__compare-pct"
                                }
                              >
                                {comparePct}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="cost-breakdown-card__track rounded-pill overflow-hidden">
                      <div
                        className="cost-breakdown-card__bar rounded-pill"
                        style={{ width: `${pct}%`, backgroundColor: row.color }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {!loading ? (
          <div className="cost-breakdown-card__footer mx-n5 px-5 py-4 mt-5 mb-0 rounded-bottom">
            <div className="d-flex align-items-center justify-content-between">
              <span className="cost-breakdown-card__footer-label">TOTAL COSTS</span>
              <span className="cost-breakdown-card__footer-value fw-bolder fs-4">
                {getAmount(-total, currencySign, { decimals: 2 })}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
